import { useEffect, useState } from "react";
import { Sparkles, Loader2, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";

const CHIPS = ["Legs", "Upper body", "Full body", "Cardio", "Core", "Carries & drags", "Easy recovery"];

export function WorkoutGenerator({ workouts, maxMinutes, onState, pendingRequest, onPendingHandled }) {
  const [text, setText] = useState("");
  const [minutes, setMinutes] = useState(Math.min(45, maxMinutes || 45));
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(workouts?.[0] || null);

  const generate = async (req) => {
    setBusy(true);
    try {
      const { data } = await api.post("/fitness/workout", { request: req ?? text, minutes: Number(minutes) });
      setCurrent(data);
      setText("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    if (!pendingRequest) return;
    generate(pendingRequest).finally(onPendingHandled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRequest]);
  const markDone = async () => {
    try { const { data } = await api.put(`/fitness/workout/${current.workout_id}/done`); setCurrent({ ...current, done: true }); onState(data); toast.success("Nice work — logged for today."); }
    catch { toast.error("Could not save that."); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="fitness-workout-generator">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h3 className="font-heading text-xl text-slate-900">Or tell the coach what you want</h3><p className="text-sm text-slate-500">Pick a body part or type it — the session is built for your equipment, your limits and your goal.</p></div>
        <label className="flex items-center gap-2 text-xs text-slate-500"><Clock className="w-3.5 h-3.5" />
          <select value={minutes} onChange={(e) => setMinutes(e.target.value)} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm text-slate-800" data-testid="fitness-workout-minutes">
            {[20, 30, 45, 60, 75].filter((m) => m <= (maxMinutes || 75)).map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
          </select>
        </label>
      </div>
      <div className="flex flex-wrap gap-2 mt-4" data-testid="fitness-workout-chips">
        {CHIPS.map((c) => <button key={c} type="button" disabled={busy} onClick={() => generate(`${c} today`)} className="text-xs font-semibold px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:border-primary hover:text-primary transition-colors disabled:opacity-50" data-testid={`fitness-workout-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{c}</button>)}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); generate(); }} className="flex gap-2 mt-3">
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder='e.g. "legs today but my knee is sore" or "quick cardio, no equipment"' className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/30" data-testid="fitness-workout-input" />
        <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="fitness-workout-generate">{busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Build it</Button>
      </form>
      {busy && <p className="text-sm text-slate-500 mt-3" data-testid="fitness-workout-busy">Building your session… about 20 seconds.</p>}
      {current && !busy && <Workout w={current} onDone={markDone} />}
    </section>
  );
}

function Workout({ w, onDone }) {
  return (
    <div className="mt-5 border-t border-slate-100 pt-5" data-testid="fitness-workout">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><p className="text-[11px] font-bold uppercase tracking-wider text-primary">{w.date} · {w.duration_min} min</p><h4 className="font-heading text-2xl text-slate-900" data-testid="fitness-workout-title">{w.title}</h4><p className="text-sm text-slate-600 mt-1">{w.focus}</p></div>
        {w.done ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600" data-testid="fitness-workout-done-badge"><CheckCircle2 className="w-4 h-4" /> Done</span> : <Button onClick={onDone} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fitness-workout-done"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark done</Button>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        <Block title="Warm-up" items={w.warmup} />
        <div className="md:col-span-2">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Main work</p>
          <table className="w-full text-sm"><tbody>{w.main.map((m, i) => (
            <tr key={i} className="border-t border-slate-100 align-top"><td className="py-1.5 pr-2 font-semibold text-slate-900">{m.name}</td><td className="py-1.5 text-slate-600 whitespace-nowrap">{m.sets} × {m.reps}</td><td className="py-1.5 text-xs text-slate-400 whitespace-nowrap pl-2">{m.rest}</td><td className="py-1.5 text-xs text-slate-500 pl-3">{m.note}</td></tr>
          ))}</tbody></table>
        </div>
        {w.finisher?.length > 0 && <Block title="Finisher" items={w.finisher} />}
        <Block title="Cool-down" items={w.cooldown} />
        <div className="md:col-span-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700"><span className="font-semibold text-slate-900">Why this:</span> {w.why} <span className="block mt-1 text-xs text-slate-500">Safety: {w.safety_tip}</span></div>
      </div>
    </div>
  );
}

function Block({ title, items }) {
  return <div><p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{title}</p><ul className="space-y-1 text-sm text-slate-700 list-disc pl-5">{(items || []).map((s, i) => <li key={i}>{s}</li>)}</ul></div>;
}
